import { useEffect, useState } from 'react';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { billingApi } from '@/api/billing.api';
import { useBilling } from '@/hooks/useBilling';
import { GUARANTEE_CLAIM_EMAIL } from '@/utils/guaranteeCopy';
import {
  CANCELLATION_DETAIL_MAX_LENGTH,
  CANCELLATION_REASONS,
  CANCELLATION_REASON_LABELS,
  type CancellationReason,
} from '@/types/cancellation.types';
import type { UsageAnalytics } from '@/types/billing.types';

interface CancellationInterviewModalProps {
  isOpen: boolean;
  /** Dismiss without proceeding — the merchant changed their mind. */
  onClose: () => void;
  /**
   * Hand the merchant off to wherever cancellation actually happens. Called
   * AFTER the answer has been queued, never awaited on it.
   */
  onProceed: () => void;
}

/** Percentage or an honest dash — never a fabricated 0%. */
function formatRate(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white/70 p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

/**
 * Cancel-intent exit interview (MONETIZATION_STRATEGY_HORMOZI.md §9 item L,
 * §7 "Exit interview save").
 *
 * Two jobs, in the doc's order: show the merchant their OWN AR performance and
 * make a save offer, then — and only then — let the cancel proceed.
 *
 * Three rules this component must never break:
 *  1. **The cancel always wins.** Every exit path calls `onProceed`/`onClose`
 *     synchronously. The feedback POST is fired and abandoned; its promise is
 *     never awaited and its rejection is swallowed inside `billingApi`.
 *  2. **Skippable.** "Continue to cancel" is live from the first render with no
 *     reason selected. Nothing here is a required field.
 *  3. **No failure surface.** A merchant who is leaving must never see an error
 *     that could be mistaken for "your cancellation failed".
 */
export function CancellationInterviewModal({
  isOpen,
  onClose,
  onProceed,
}: CancellationInterviewModalProps) {
  const { plan } = useBilling();
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [detail, setDetail] = useState('');
  const [analytics, setAnalytics] = useState<UsageAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Their own numbers are the whole point of the interstitial, but they are
  // also the one part that can be slow or unavailable. Load them lazily and
  // render the interview regardless — a missing stat block must not stop a
  // merchant reaching the cancel button.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setAnalyticsLoading(true);
    void (async () => {
      const data = await billingApi.getUsageAnalytics();
      if (cancelled) return;
      setAnalytics(data);
      setAnalyticsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const funnel = analytics?.funnel;
  const hasNumbers = !!funnel && !analytics?.no_data_yet;

  /**
   * Fire-and-forget. Deliberately NOT awaited and deliberately not
   * `try`/`catch`ed here — `billingApi.submitCancellationFeedback` already
   * resolves rather than rejects on failure, so there is no unhandled
   * rejection and no path from a network error to the merchant's screen.
   */
  const queueFeedback = () => {
    if (!reason) return;
    void billingApi.submitCancellationFeedback({
      reason,
      // Empty string would persist as an empty row value; the server maps
      // blank to null, but sending undefined keeps the wire honest.
      detail: detail.trim() ? detail.trim() : undefined,
    });
  };

  const handleProceed = () => {
    queueFeedback();
    onProceed();
  };

  const handleSkip = () => {
    // No answer, no POST — an unanswered interview is not data.
    onProceed();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/80 bg-white p-6 shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
        >
          <X className="h-4 w-4" />
        </button>

        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
          Before you cancel
        </span>
        <h2 className="mt-3 text-xl font-semibold text-slate-900">
          Here&apos;s what AR did for your store
        </h2>

        {/* §7: "show them their own views/sessions before the cancel button
            works its way to the end." */}
        {analyticsLoading ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[68px] animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : hasNumbers ? (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <Stat label="Viewer loads" value={funnel!.viewer_loaded} />
              <Stat label="AR launches" value={funnel!.ar_launched} />
              <Stat label="Add to cart" value={funnel!.add_to_cart} />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              This billing period · {formatRate(funnel!.ar_to_cart_rate)} of AR launches
              reached the cart.
            </p>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-500">
            We don&apos;t have storefront activity for this period yet — which usually
            means the AR button never went live on your product pages. That&apos;s the
            first thing we&apos;d fix.
          </p>
        )}

        {/* Save offer (§7 "Exit interview save": the 15-minute review). */}
        <div className="mt-5 rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
            <div>
              <p className="text-sm font-semibold text-indigo-900">
                Want 15 minutes with us before you go?
              </p>
              <p className="mt-1 text-xs text-indigo-700">
                We&apos;ll review your AR performance with you and fix what&apos;s not
                working — at no charge, whether or not you stay. Email{' '}
                <a
                  href={`mailto:${GUARANTEE_CLAIM_EMAIL}?subject=${encodeURIComponent(
                    'AR performance review before I cancel',
                  )}`}
                  className="font-semibold underline"
                >
                  {GUARANTEE_CLAIM_EMAIL}
                </a>{' '}
                and we&apos;ll book it.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-900">
            What made you decide to cancel?
          </p>
          <p className="mt-1 text-xs text-slate-500">Optional — this is just for us.</p>

          <div className="mt-3 space-y-2">
            {CANCELLATION_REASONS.map((value) => (
              <label
                key={value}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm transition ${
                  reason === value
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="cancellation-reason"
                  value={value}
                  checked={reason === value}
                  onChange={() => setReason(value)}
                  className="h-4 w-4 accent-indigo-500"
                />
                {CANCELLATION_REASON_LABELS[value]}
              </label>
            ))}
          </div>

          {reason && (
            <div className="mt-3">
              <textarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                maxLength={CANCELLATION_DETAIL_MAX_LENGTH}
                rows={3}
                placeholder="Anything else we should know? (optional)"
                className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-700 outline-none transition focus:border-indigo-300"
              />
              <p className="mt-1 text-right text-[11px] text-slate-400">
                {detail.length}/{CANCELLATION_DETAIL_MAX_LENGTH}
              </p>
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Keep {plan?.plan?.name ?? 'my plan'}
          </button>
          {/* Never disabled and never in a pending state: the merchant's exit
              must not depend on a request we may not be able to make. */}
          <button
            onClick={reason ? handleProceed : handleSkip}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            {reason ? 'Send & continue to cancel' : 'Continue to cancel'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CancellationInterviewModal;
